import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { AuthedImage } from '@/components/shared/AuthedImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { openSecureMedia } from '@/lib/secureMedia';
import { ArrowLeft, Save, User, Briefcase, Shield, FileText, CheckCircle2, Clock, Eye, Trash2, Edit2, Upload, Plus, Mail, Key, MapPin, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function StaffDetailPage() {
  const { staffId } = useParams();
  const [tabParam] = useSearchParams();   // NAV-1: ?tab=compliance deep link
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({});
  const [hrForm, setHrForm] = useState({});
  const [onboardingRecord, setOnboardingRecord] = useState(null);
  const [staffDocs, setStaffDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignForm, setReassignForm] = useState({ state_id: '', branch_id: '', reason: '' });
  const [branches, setBranches] = useState([]);
  const [states, setStates] = useState([]);
  const [editing, setEditing] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [uploadDocDialog, setUploadDocDialog] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({ document_type: 'OTHER', document_number: '', issue_date: '', expiry_date: '', file: null });
  const [editingSnapshot, setEditingSnapshot] = useState(false);
  const [snapshotForm, setSnapshotForm] = useState({});
  const [payrollForm, setPayrollForm] = useState({});
  const [payrollLoaded, setPayrollLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/staff/${staffId}`);
      setStaff(res.data.data);
      setForm(res.data.data || {});
      setHrForm(res.data.data || {});

      // Load onboarding record
      const [obR, docR, brR, stR] = await Promise.allSettled([
        api.get(`/staff/${staffId}/onboarding-record`),
        api.get(`/staff/${staffId}/documents`),
        api.get('/org/branches'),
        api.get('/org/states'),
      ]);
      if (obR.status === 'fulfilled') setOnboardingRecord(obR.value.data.data);
      if (docR.status === 'fulfilled') setStaffDocs(docR.value.data.items || docR.value.data.data || []);
      if (brR.status === 'fulfilled') setBranches(brR.value.data.data || []);
      if (stR.status === 'fulfilled') setStates(stR.value.data.data || []);
    } catch (e) { toast.error('Failed to load staff'); navigate('/staff/list'); }
  }, [staffId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Staff code = Employee ID (one value, no manual entry). When this profile's staff code is empty or out of step,
  // generate it once; users without staff.manage are refused quietly and just see what is stored.
  const staffCodeSynced = useRef(null);
  useEffect(() => {
    if (!staff || staffCodeSynced.current === staffId) return;
    if (staff.staff_code && staff.staff_code === staff.employee_id) return;
    staffCodeSynced.current = staffId;
    api.post(`/staff/${staffId}/staff-code/sync`)
      .then((res) => { if (res.data?.data?.changed) load(); })
      .catch(() => {});
  }, [staff, staffId, load]);

  const loadPayroll = useCallback(async () => {
    try {
      const res = await api.get(`/staff/${staffId}/payroll`);
      setPayrollForm(res.data.data || {});
      setPayrollLoaded(true);
    } catch (e) {
      // non-fatal — permission may not be granted
      setPayrollLoaded(true);
    }
  }, [staffId]);

  const handlePayrollSave = async () => {
    setSaving(true);
    try {
      const payload = {
        employment_type: payrollForm.employment_type,
        award_level: payrollForm.award_level,
        base_hourly_rate: parseFloat(payrollForm.base_hourly_rate) || 0,
        saturday_multiplier: parseFloat(payrollForm.saturday_multiplier) || 1.5,
        sunday_multiplier: parseFloat(payrollForm.sunday_multiplier) || 2.0,
        public_holiday_multiplier: parseFloat(payrollForm.public_holiday_multiplier) || 2.5,
        overtime_daily_threshold_hours: parseFloat(payrollForm.overtime_daily_threshold_hours) || 10,
        overtime_weekly_threshold_hours: parseFloat(payrollForm.overtime_weekly_threshold_hours) || 38,
        overtime_multiplier: parseFloat(payrollForm.overtime_multiplier) || 1.5,
        night_shift_loading_percent: parseFloat(payrollForm.night_shift_loading_percent) || 0,
        sleepover_flat_rate: parseFloat(payrollForm.sleepover_flat_rate) || 0,
        broken_shift_allowance: parseFloat(payrollForm.broken_shift_allowance) || 0,
        travel_allowance_enabled: !!payrollForm.travel_allowance_enabled,
        payroll_external_id: payrollForm.payroll_external_id || '',
        payroll_provider: payrollForm.payroll_provider || 'NONE',
      };
      const res = await api.put(`/staff/${staffId}/payroll`, payload);
      setPayrollForm(res.data.data || {});
      toast.success('Payroll & Award settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save payroll settings'); }
    setSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // The bio is saved with the staff record (staff.manage). It used to go through the account-admin /admin/users
      // endpoint, whose user-management rules can refuse HR — showing "Failed" after the qualifications had saved.
      await api.put(`/staff/${staffId}`, {
        compliance_status: form.compliance_status,
        qualifications: form.qualifications,
        profile: form.profile || '',
      });
      if (staff.user_id && photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/admin/users/${staff.user_id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotoFile(null);
      }
      toast.success('Profile updated');
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const [generatingId, setGeneratingId] = useState(false);
  const handleGenerateEmployeeId = async () => {
    setGeneratingId(true);
    try {
      const res = await api.post(`/staff/${staffId}/employee-id/generate`);
      toast.success(`Employee ID assigned: ${res.data.data.employee_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.error?.message || e.response?.data?.detail || 'Failed to generate Employee ID'); }
    setGeneratingId(false);
  };

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5 MB'); return; }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  // Prefer the canonical staff-collection photo (same as the staff dashboard
  // "My Profile" tab), falling back to the legacy users-collection photo.
  const photoSrc = photoPreview || staff?.photo_url || staff?.profile_photo_url || null;
  // Signed / blob URLs can be rendered directly; auth-gated legacy URLs need
  // the authenticated image component. When no photo exists, fall back to the
  // initial-letter placeholder.
  const renderPhoto = (src, alt, className, fallback) => {
    if (!src) return fallback || null;
    const isBlob = src.startsWith('blob:') || src.startsWith('data:');
    const isSigned = src.startsWith('/api/uploads/');
    if (isBlob || isSigned) {
      return <img src={isBlob ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`} alt={alt} className={className} />;
    }
    return <AuthedImage src={src} alt={alt} className={className} />;
  };

  const handleHrSave = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/${staffId}`, {
        // save what the selects show (they display CASUAL / SUPPORT WORKER when nothing is stored yet)
        employment_type: hrForm.employment_type || 'CASUAL', department_id: hrForm.department_id,
        start_date: hrForm.start_date, role_title: hrForm.role_title,
        staff_category: hrForm.staff_category || 'SUPPORT_WORKER',
      });
      toast.success('HR details updated');
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/staff/${staffId}`);
      toast.success('Staff profile deleted');
      navigate('/staff/list');
    } catch (e) { toast.error(e.response?.data?.detail || e.response?.data?.error?.message || 'Delete failed'); }
  };

  const handleUploadDocument = async () => {
    if (!docUploadForm.file) { toast.error('Please select a file'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('staff_id', staffId);
      fd.append('document_type', docUploadForm.document_type);
      fd.append('file', docUploadForm.file);
      if (docUploadForm.document_number) fd.append('document_number', docUploadForm.document_number);
      if (docUploadForm.issue_date) fd.append('issue_date', docUploadForm.issue_date);
      if (docUploadForm.expiry_date) fd.append('expiry_date', docUploadForm.expiry_date);
      await api.post(`/staff/${staffId}/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadDocDialog(false);
      setDocUploadForm({ document_type: 'OTHER', document_number: '', issue_date: '', expiry_date: '', file: null });
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Upload failed'); }
    setSaving(false);
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await api.delete(`/staff/${staffId}/documents/${docId}`);
      toast.success('Document deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleSendResetPassword = async () => {
    if (!staff.user_id) { toast.error('No user account linked'); return; }
    try {
      const res = await api.post(`/auth/admin/users/${staff.user_id}/send-reset-password`);
      toast.success(res.data.message || 'Reset link sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send reset'); }
  };

  const handleSendPasswordSetup = async () => {
    if (!staff.user_id) { toast.error('No user account linked'); return; }
    try {
      const res = await api.post(`/auth/admin/users/${staff.user_id}/send-password-setup`);
      toast.success(res.data.message || 'Setup link sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send setup link'); }
  };

  const handleSaveSnapshot = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/${staffId}/onboarding-record`, { application_snapshot: snapshotForm });
      toast.success('Application snapshot updated');
      setEditingSnapshot(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleReassign = async () => {
    if (!reassignForm.state_id && !reassignForm.branch_id) { toast.error('Select a state or branch'); return; }
    setSaving(true);
    try {
      await api.post(`/staff/${staffId}/reassign`, reassignForm);
      toast.success('Staff reassigned successfully');
      setReassignOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Reassign failed'); }
    setSaving(false);
  };

  if (!staff) return null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="staff-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/staff/list')} className="-ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            {renderPhoto(photoSrc, staff.full_name, 'h-full w-full rounded-full object-cover',
              <AvatarFallback>{(staff.full_name || staff.first_name || '?').charAt(0).toUpperCase()}</AvatarFallback>)}
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{staff.full_name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || 'Staff'}</h1>
            <p className="text-muted-foreground">{staff.email} · {staff.staff_code || staff.employee_id || '-'}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid="edit-staff-btn"><Edit2 className="h-3 w-3 mr-1" />{editing ? 'Cancel Edit' : 'Edit'}</Button>
            <Button size="sm" variant="outline" onClick={handleSendResetPassword} data-testid="reset-password-btn"><Key className="h-3 w-3 mr-1" />Reset Password</Button>
            <Button size="sm" variant="outline" onClick={handleSendPasswordSetup} data-testid="setup-password-btn"><Mail className="h-3 w-3 mr-1" />Send Setup Link</Button>
            <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(true)} data-testid="delete-staff-btn"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
          </div>
          <Badge variant={staff.compliance_status === 'COMPLIANT' ? 'default' : 'destructive'}>{staff.compliance_status || 'UNKNOWN'}</Badge>
          {staff.status && <Badge variant="outline">{staff.status}</Badge>}
          {staff.source_candidate_id && <Link to={`/hr/candidates/${staff.source_candidate_id}`} className="text-xs text-primary underline">View Candidate Profile →</Link>}
        </div>
      </div>

      <Tabs defaultValue={['profile', 'hr', 'payroll', 'compliance'].includes(tabParam.get('tab')) ? tabParam.get('tab') : 'profile'}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1" />Profile</TabsTrigger>
          <TabsTrigger value="hr" data-testid="tab-hr"><Briefcase className="w-4 h-4 mr-1" />Employment</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll" onClick={() => { if (!payrollLoaded) loadPayroll(); }}><DollarSign className="w-4 h-4 mr-1" />Payroll & Award</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance"><Shield className="w-4 h-4 mr-1" />Compliance</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding"><CheckCircle2 className="w-4 h-4 mr-1" />Onboarding</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1" />Documents ({staffDocs.length})</TabsTrigger>
        </TabsList>

        {/* PROFILE TAB */}
        <TabsContent value="profile" className="space-y-4 mt-4">
          <Card><CardContent className="space-y-4 pt-4">
            {editing && (
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {renderPhoto(photoSrc, staff.full_name, 'h-full w-full rounded-full object-cover',
                    <AvatarFallback>{(staff.full_name || '?').charAt(0).toUpperCase()}</AvatarFallback>)}
                </Avatar>
                <div className="space-y-1">
                  <Label>Photo</Label>
                  <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onPhotoChange} data-testid="staff-detail-photo-input" />
                  <p className="text-xs text-muted-foreground">JPEG, PNG or WebP, max 5 MB</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID</Label>
                <div className="flex gap-2">
                  <Input value={staff.employee_id || ''} readOnly className="bg-muted font-mono" placeholder="Not generated yet" data-testid="employee-id-input" />
                  {!staff.employee_id && (
                    <Button type="button" variant="outline" size="sm" className="h-10 whitespace-nowrap" onClick={handleGenerateEmployeeId} disabled={generatingId} data-testid="generate-employee-id-btn">
                      {generatingId ? 'Generating…' : 'Generate ID'}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-testid="employee-id-hint">
                  Auto-generated from the branch code{staff.branch_id ? ` (${staff.branch_id}-0001…)` : ' — assign a branch first for a branch-based ID, otherwise EMP-0001…'}
                </p>
              </div>
              <div><Label>Compliance Status</Label>
                <Select value={form.compliance_status || 'COMPLIANT'} onValueChange={v => setForm({ ...form, compliance_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="COMPLIANT">Compliant</SelectItem><SelectItem value="NON_COMPLIANT">Non-Compliant</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Qualifications</Label><Input value={(form.qualifications || []).join(', ')} onChange={e => setForm({ ...form, qualifications: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} /></div>
            <div><Label>My Profile</Label><Textarea value={form.profile || ''} onChange={e => setForm({ ...form, profile: e.target.value })} rows={3} placeholder="Short bio / about this staff member" data-testid="staff-detail-profile-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Branch</Label><Input value={staff.branch_id || ''} readOnly className="bg-muted" /></div>
              <div><Label>State</Label><Input value={staff.state_id || ''} readOnly className="bg-muted" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
              <Button variant="outline" onClick={() => { setReassignForm({ state_id: staff.state_id || '', branch_id: staff.branch_id || '', reason: '' }); setReassignOpen(true); }} data-testid="reassign-btn"><MapPin className="h-4 w-4 mr-2" />Reassign Branch/State</Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* HR EMPLOYMENT TAB */}
        <TabsContent value="hr" className="space-y-4 mt-4">
          <Card><CardHeader><CardTitle className="text-base">Employment Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Staff Code</Label><Input value={staff.staff_code || staff.employee_id || ''} readOnly className="bg-muted font-mono" placeholder="Generated automatically" data-testid="staff-code-input" />
                  <p className="text-xs text-muted-foreground mt-1">Same as the Employee ID, generated automatically.</p></div>
                <div><Label>Role Title</Label><Input value={hrForm.role_title || ''} onChange={e => setHrForm({ ...hrForm, role_title: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Employment Type</Label>
                  <Select value={hrForm.employment_type || 'CASUAL'} onValueChange={v => setHrForm({ ...hrForm, employment_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['FULL_TIME', 'PART_TIME', 'CASUAL', 'CONTRACT'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Staff Category</Label>
                  <Select value={hrForm.staff_category || 'SUPPORT_WORKER'} onValueChange={v => setHrForm({ ...hrForm, staff_category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['SUPPORT_WORKER', 'NURSE', 'ADMIN', 'HR', 'FINANCE', 'OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Start Date</Label><Input type="date" value={hrForm.start_date || ''} onChange={e => setHrForm({ ...hrForm, start_date: e.target.value })} /></div>
                <div><Label>Status</Label><Input value={staff.status || 'ACTIVE'} readOnly className="bg-muted" /></div>
              </div>
              {staff.address_json && Object.keys(staff.address_json).length > 0 && (
                <div className="pt-3 border-t"><Label className="text-xs text-muted-foreground">Address (from onboarding)</Label>
                  <p className="text-sm mt-1">{Object.values(staff.address_json).filter(Boolean).join(', ') || '-'}</p>
                </div>
              )}
              {staff.emergency_contact_json && Object.keys(staff.emergency_contact_json).length > 0 && (
                <div><Label className="text-xs text-muted-foreground">Emergency Contact</Label>
                  <p className="text-sm mt-1">{Object.values(staff.emergency_contact_json).filter(Boolean).join(' · ') || '-'}</p>
                </div>
              )}
              <Button onClick={handleHrSave} disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Employment'}</Button>
            </CardContent>
          </Card>

          {/* Bank & Payroll (from onboarding snapshot) */}
          {onboardingRecord?.application_snapshot?.bank_details && Object.keys(onboardingRecord.application_snapshot.bank_details).length > 0 && (
            <Card><CardHeader><CardTitle className="text-base">Bank & Payroll (from onboarding)</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                {Object.entries(onboardingRecord.application_snapshot.bank_details).map(([k, v]) => v ? (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span><span className="font-mono">{k.includes('tfn') || k.includes('account') ? '****' + String(v).slice(-4) : v}</span></div>
                ) : null)}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PAYROLL & AWARD TAB */}
        <TabsContent value="payroll" className="space-y-4 mt-4" data-testid="payroll-tab-content">
          <Card><CardHeader><CardTitle className="text-base">Employment & Award Classification</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Employment Type</Label>
                  <Select value={payrollForm.employment_type || 'CASUAL'} onValueChange={v => setPayrollForm({ ...payrollForm, employment_type: v })}>
                    <SelectTrigger data-testid="payroll-employment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['FULL_TIME', 'PART_TIME', 'CASUAL'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Award Level</Label>
                  <Input value={payrollForm.award_level || ''} onChange={e => setPayrollForm({ ...payrollForm, award_level: e.target.value })} placeholder="e.g. SCHADS Level 2" data-testid="payroll-award-level" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Base Hourly Rate (AUD)</Label>
                  <Input type="number" step="0.01" min="0" value={payrollForm.base_hourly_rate ?? ''} onChange={e => setPayrollForm({ ...payrollForm, base_hourly_rate: e.target.value })} placeholder="0.00" data-testid="payroll-base-rate" />
                </div>
                <div><Label>Sleepover Flat Rate (AUD)</Label>
                  <Input type="number" step="0.01" min="0" value={payrollForm.sleepover_flat_rate ?? ''} onChange={e => setPayrollForm({ ...payrollForm, sleepover_flat_rate: e.target.value })} placeholder="0.00" data-testid="payroll-sleepover-rate" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Weekend & Public Holiday Penalties</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Saturday Multiplier</Label>
                  <Input type="number" step="0.1" min="1" value={payrollForm.saturday_multiplier ?? 1.5} onChange={e => setPayrollForm({ ...payrollForm, saturday_multiplier: e.target.value })} data-testid="payroll-sat-mult" />
                  <p className="text-[10px] text-muted-foreground mt-1">Default: 1.5x</p>
                </div>
                <div><Label>Sunday Multiplier</Label>
                  <Input type="number" step="0.1" min="1" value={payrollForm.sunday_multiplier ?? 2.0} onChange={e => setPayrollForm({ ...payrollForm, sunday_multiplier: e.target.value })} data-testid="payroll-sun-mult" />
                  <p className="text-[10px] text-muted-foreground mt-1">Default: 2.0x</p>
                </div>
                <div><Label>Public Holiday Multiplier</Label>
                  <Input type="number" step="0.1" min="1" value={payrollForm.public_holiday_multiplier ?? 2.5} onChange={e => setPayrollForm({ ...payrollForm, public_holiday_multiplier: e.target.value })} data-testid="payroll-ph-mult" />
                  <p className="text-[10px] text-muted-foreground mt-1">Default: 2.5x</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Overtime Rules</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Daily Threshold (hours)</Label>
                  <Input type="number" step="0.5" min="0" value={payrollForm.overtime_daily_threshold_hours ?? 10} onChange={e => setPayrollForm({ ...payrollForm, overtime_daily_threshold_hours: e.target.value })} data-testid="payroll-ot-daily" />
                  <p className="text-[10px] text-muted-foreground mt-1">OT after this many hours/day</p>
                </div>
                <div><Label>Weekly Threshold (hours)</Label>
                  <Input type="number" step="0.5" min="0" value={payrollForm.overtime_weekly_threshold_hours ?? 38} onChange={e => setPayrollForm({ ...payrollForm, overtime_weekly_threshold_hours: e.target.value })} data-testid="payroll-ot-weekly" />
                  <p className="text-[10px] text-muted-foreground mt-1">OT after this many hours/week</p>
                </div>
                <div><Label>Overtime Multiplier</Label>
                  <Input type="number" step="0.1" min="1" value={payrollForm.overtime_multiplier ?? 1.5} onChange={e => setPayrollForm({ ...payrollForm, overtime_multiplier: e.target.value })} data-testid="payroll-ot-mult" />
                  <p className="text-[10px] text-muted-foreground mt-1">Default: 1.5x</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Allowances & Loadings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Night Shift Loading (%)</Label>
                  <Input type="number" step="1" min="0" value={payrollForm.night_shift_loading_percent ?? 0} onChange={e => setPayrollForm({ ...payrollForm, night_shift_loading_percent: e.target.value })} data-testid="payroll-night-loading" />
                </div>
                <div><Label>Broken Shift Allowance (AUD)</Label>
                  <Input type="number" step="0.01" min="0" value={payrollForm.broken_shift_allowance ?? 0} onChange={e => setPayrollForm({ ...payrollForm, broken_shift_allowance: e.target.value })} data-testid="payroll-broken-shift" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input type="checkbox" checked={!!payrollForm.travel_allowance_enabled} onChange={e => setPayrollForm({ ...payrollForm, travel_allowance_enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300" data-testid="payroll-travel-allowance" />
                    <span className="text-sm">Travel Allowance Enabled</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Payroll Integration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Payroll Provider</Label>
                  <Select value={payrollForm.payroll_provider || 'NONE'} onValueChange={v => setPayrollForm({ ...payrollForm, payroll_provider: v })}>
                    <SelectTrigger data-testid="payroll-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['NONE', 'XERO', 'EMPLOYMENT_HERO', 'MYOB'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>External Payroll ID</Label>
                  <Input value={payrollForm.payroll_external_id || ''} onChange={e => setPayrollForm({ ...payrollForm, payroll_external_id: e.target.value })} placeholder="e.g. XR-001" data-testid="payroll-external-id" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Payroll is operational only — never affects invoices, funding, or NDIS claims.</p>
            </CardContent>
          </Card>

          <Button onClick={handlePayrollSave} disabled={saving} data-testid="save-payroll-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Payroll Settings'}
          </Button>
        </TabsContent>

        {/* COMPLIANCE TAB */}
        <TabsContent value="compliance" className="space-y-4 mt-4">
          {staffDocs.filter(d => ['COMPLIANCE', 'onboarding_conversion'].includes(d.category || d.source)).length > 0 ? (
            <div className="space-y-2">
              {staffDocs.filter(d => ['COMPLIANCE', 'onboarding_conversion'].includes(d.category || d.source)).map(d => {
                const expiry = d.expiry_date?.slice(0, 10);
                let daysLeft = null;
                let expiryColor = '';
                if (expiry) {
                  daysLeft = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
                  if (daysLeft < 0) expiryColor = 'text-red-600 bg-red-50';
                  else if (daysLeft <= 7) expiryColor = 'text-red-500 bg-red-50';
                  else if (daysLeft <= 30) expiryColor = 'text-amber-600 bg-amber-50';
                  else if (daysLeft <= 60) expiryColor = 'text-yellow-600 bg-yellow-50';
                }
                return (
                  <Card key={d.id} className={daysLeft !== null && daysLeft <= 7 ? 'border-red-300' : daysLeft !== null && daysLeft <= 30 ? 'border-amber-200' : ''}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant={d.compliance_status === 'APPROVED' ? 'default' : 'destructive'}>{d.compliance_status || d.status}</Badge>
                          <div>
                            <p className="text-sm font-medium">{d.document_type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">
                              {d.document_number ? `#${d.document_number} · ` : ''}
                              {d.source && `Source: ${d.source}`}
                            </p>
                          </div>
                        </div>
                        {d.candidate_document_id && <Badge variant="outline" className="text-xs">From Onboarding</Badge>}
                      </div>
                      {expiry && (
                        <div className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${expiryColor || 'text-muted-foreground bg-muted/50'}`}>
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium">Expiry: {expiry}</span>
                          <span>·</span>
                          {daysLeft < 0 ? (
                            <span className="font-bold">EXPIRED ({Math.abs(daysLeft)} days ago)</span>
                          ) : daysLeft === 0 ? (
                            <span className="font-bold">EXPIRES TODAY</span>
                          ) : daysLeft <= 7 ? (
                            <span className="font-bold">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining — CRITICAL</span>
                          ) : daysLeft <= 30 ? (
                            <span className="font-semibold">{daysLeft} days remaining — URGENT</span>
                          ) : daysLeft <= 60 ? (
                            <span>{daysLeft} days remaining — WARNING</span>
                          ) : (
                            <span>{daysLeft} days remaining</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : <p className="text-center text-muted-foreground py-6">No compliance documents</p>}
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding" className="space-y-4 mt-4">
          {onboardingRecord ? (
            <>
              <Card><CardHeader><CardTitle className="text-base">Source Reference</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Source Candidate:</span> <Link to={`/hr/candidates/${onboardingRecord.source_candidate_id}`} className="text-primary underline">{onboardingRecord.source_candidate_id?.slice(0, 12)}...</Link></div>
                    <div><span className="text-muted-foreground">Converted At:</span> {onboardingRecord.conversion_at?.slice(0, 16)}</div>
                    <div><span className="text-muted-foreground">Approved At:</span> {onboardingRecord.approved_at?.slice(0, 16)}</div>
                    <div><span className="text-muted-foreground">Converted By:</span> {onboardingRecord.conversion_by?.slice(0, 12)}...</div>
                  </div>
                </CardContent>
              </Card>

              {onboardingRecord.application_snapshot && (
                <Card><CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base">Application Snapshot</CardTitle>
                    {!editingSnapshot ? (
                      <Button size="sm" variant="outline" onClick={() => { setSnapshotForm(JSON.parse(JSON.stringify(onboardingRecord.application_snapshot))); setEditingSnapshot(true); }} data-testid="edit-snapshot-btn"><Edit2 className="h-3 w-3 mr-1" />Edit</Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingSnapshot(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleSaveSnapshot} disabled={saving} data-testid="save-snapshot-btn"><Save className="h-3 w-3 mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
                      </div>
                    )}
                  </div>
                  {editingSnapshot && <p className="text-xs text-amber-600 mt-1">Changes will be audit logged.</p>}
                </CardHeader>
                  <CardContent>
                    {Object.entries(editingSnapshot ? snapshotForm : onboardingRecord.application_snapshot).map(([key, data]) => {
                      if (!data || Object.keys(data).length === 0) return null;
                      return (
                        <details key={key} className="border rounded-lg p-2 mb-2" open={editingSnapshot}>
                          <summary className="text-xs font-medium cursor-pointer">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</summary>
                          <div className="mt-1 space-y-1 text-xs">
                            {Object.entries(data).map(([k, v]) => {
                              if (editingSnapshot) {
                                return (
                                  <div key={k} className="grid grid-cols-3 gap-2 items-center">
                                    <Label className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}</Label>
                                    <Input className="col-span-2 h-7 text-xs" value={typeof v === 'boolean' ? (v ? 'true' : 'false') : Array.isArray(v) ? v.join(', ') : String(v || '')}
                                      onChange={e => setSnapshotForm(prev => ({ ...prev, [key]: { ...prev[key], [k]: e.target.value } }))} />
                                  </div>
                                );
                              }
                              return v ? (
                                <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span><span>{Array.isArray(v) ? v.join(', ') : String(v)}</span></div>
                              ) : null;
                            })}
                          </div>
                        </details>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {onboardingRecord.declarations_snapshot && Object.keys(onboardingRecord.declarations_snapshot).length > 0 && (
                <Card><CardHeader><CardTitle className="text-base">Declarations</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {Object.entries(onboardingRecord.declarations_snapshot).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        {v ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Clock className="h-4 w-4 text-muted-foreground" />}
                        <span>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <p className="text-center text-muted-foreground py-6">No onboarding record — this staff member was not created through the onboarding portal</p>
          )}
        </TabsContent>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{staffDocs.length} documents</p>
            <Button size="sm" onClick={() => setUploadDocDialog(true)} data-testid="add-staff-doc-btn"><Plus className="h-3 w-3 mr-1" />Add Document</Button>
          </div>
          {staffDocs.length > 0 ? (
            <div className="space-y-2">
              {staffDocs.map(d => {
                const expiry = d.expiry_date?.slice(0, 10);
                let daysLeft = null;
                let expiryColor = '';
                if (expiry) {
                  daysLeft = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
                  if (daysLeft < 0) expiryColor = 'text-red-600 bg-red-50';
                  else if (daysLeft <= 7) expiryColor = 'text-red-500 bg-red-50';
                  else if (daysLeft <= 30) expiryColor = 'text-amber-600 bg-amber-50';
                  else if (daysLeft <= 60) expiryColor = 'text-yellow-600 bg-yellow-50';
                }
                return (
                  <Card key={d.id} className={daysLeft !== null && daysLeft <= 7 ? 'border-red-300' : daysLeft !== null && daysLeft <= 30 ? 'border-amber-200' : ''}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant={d.compliance_status === 'APPROVED' ? 'default' : 'outline'}>{d.compliance_status || 'PENDING'}</Badge>
                          <div>
                            <p className="text-sm font-medium">{d.document_type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">{d.file_name || '-'}{d.document_number ? ` · #${d.document_number}` : ''} · Source: {d.source || 'manual'}</p>
                          </div>
                        </div>
                        {d.storage_object_key && (
                          <Button size="sm" variant="ghost" onClick={async () => {
                            try { await openSecureMedia(d.storage_object_key); }
                            catch { toast.error('Unable to open this document'); }
                          }} aria-label="View document">
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => handleDeleteDocument(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                      {expiry && (
                        <div className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${expiryColor || 'text-muted-foreground bg-muted/50'}`}>
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium">Expiry: {expiry}</span>
                          <span>·</span>
                          {daysLeft < 0 ? (
                            <span className="font-bold">EXPIRED ({Math.abs(daysLeft)} days ago)</span>
                          ) : daysLeft === 0 ? (
                            <span className="font-bold">EXPIRES TODAY</span>
                          ) : daysLeft <= 7 ? (
                            <span className="font-bold">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining — CRITICAL</span>
                          ) : daysLeft <= 30 ? (
                            <span className="font-semibold">{daysLeft} days remaining — URGENT</span>
                          ) : daysLeft <= 60 ? (
                            <span>{daysLeft} days remaining — WARNING</span>
                          ) : (
                            <span>{daysLeft} days remaining</span>
                          )}
                        </div>
                      )}
                      {d.issue_date && (
                        <p className="text-[11px] text-muted-foreground mt-1 ml-1">Issued: {d.issue_date?.slice(0, 10)}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : <p className="text-center text-muted-foreground py-6">No documents</p>}
        </TabsContent>
      </Tabs>

      {/* Delete Confirm */}
      <ConfirmDialog open={deleteConfirm} onOpenChange={setDeleteConfirm} title="Delete Staff Profile" description={`Are you sure you want to delete ${staff.full_name || staff.first_name + ' ' + staff.last_name}? This action cannot be undone.`} confirmLabel="Delete" variant="destructive" onConfirm={handleDelete} />

      {/* Upload Document Dialog */}
      <Dialog open={uploadDocDialog} onOpenChange={setUploadDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Document Type</Label>
              <Select value={docUploadForm.document_type} onValueChange={v => setDocUploadForm({ ...docUploadForm, document_type: v })}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['RESUME','PHOTO_ID','NDIS_WORKER_SCREENING','WWCC','POLICE_CHECK','FIRST_AID','CPR','DRIVER_LICENCE','VEHICLE_REGISTRATION','VEHICLE_INSURANCE','QUALIFICATIONS','EMPLOYMENT_AGREEMENT','CODE_OF_CONDUCT','CONFIDENTIALITY_AGREEMENT','NDIS_DECLARATION','OTHER'].map(t => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Document Number</Label><Input value={docUploadForm.document_number} onChange={e => setDocUploadForm({ ...docUploadForm, document_number: e.target.value })} placeholder="Optional" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue Date</Label><Input type="date" value={docUploadForm.issue_date} onChange={e => setDocUploadForm({ ...docUploadForm, issue_date: e.target.value })} /></div>
              <div><Label>Expiry Date</Label><Input type="date" value={docUploadForm.expiry_date} onChange={e => setDocUploadForm({ ...docUploadForm, expiry_date: e.target.value })} /></div>
            </div>
            <div><Label>File</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setDocUploadForm({ ...docUploadForm, file: e.target.files?.[0] || null })} data-testid="doc-file-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDocDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={saving || !docUploadForm.file} data-testid="upload-doc-btn">{saving ? 'Uploading...' : 'Upload'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Branch/State Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reassign Branch & State</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg text-sm">
              <div><span className="text-muted-foreground">Current State:</span> <span className="font-medium">{staff?.state_id || '-'}</span></div>
              <div><span className="text-muted-foreground">Current Branch:</span> <span className="font-medium">{staff?.branch_id || '-'}</span></div>
            </div>
            <div><Label>New State</Label>
              <Select value={reassignForm.state_id} onValueChange={v => setReassignForm({ ...reassignForm, state_id: v })}>
                <SelectTrigger data-testid="reassign-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>New Branch</Label>
              <Select value={reassignForm.branch_id} onValueChange={v => setReassignForm({ ...reassignForm, branch_id: v })}>
                <SelectTrigger data-testid="reassign-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason</Label><Input value={reassignForm.reason} onChange={e => setReassignForm({ ...reassignForm, reason: e.target.value })} placeholder="e.g. Transferred to new branch" data-testid="reassign-reason" /></div>
            <p className="text-xs text-amber-600">Historical records (shifts, timesheets, payroll, incidents) will not be modified. Only the staff profile assignment will change.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button onClick={handleReassign} disabled={saving} data-testid="confirm-reassign">{saving ? 'Reassigning...' : 'Confirm Reassignment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
